// Baseline Comparison panel — ONE derivation for the Alerts and Test Run Results pages (CLAUDE.md rule #13).
//
// Since 2026-10-01 the detection pipeline computes the comparison at alert time and stores it on the row as
// `baseline_comparison_json` (schema_version 1; contract: Pipeline handoff
// 2026-09-30_Reply-Baseline-Comparison-Shape-Confirmed-Deploy-Date.md, built to our
// handoff/2026-09-29_Reply-Baseline-Comparison-At-Alert-Time-Portal-to-Pipeline.md). When it is present the
// panel shows the pipeline's rows, verdicts and counts as they are. That is the record of what the analyst
// (and the notification) saw at alert time.
//
// Two things stay computed here:
//   1. FALLBACK — rows written before the pipeline change (or an unknown schema_version) have no stored
//      comparison. buildBaselineComparison() produces the SAME v1 shape from baseline_context_json, so one
//      renderer serves both. It is a port of the pipeline's build_baseline_comparison_json
//      (notebooks/_detection_lib.py), so a fallback row and a stored row reach the same verdict on the same
//      input; scripts/baselineComparison.test.mjs checks that against real stored rows when tmp/ has them.
//   2. REFRESH — "Refresh IP enrichment" changes only the Country, Provider, State and City event values. The
//      stored JSON is never overwritten; those four rows are re-evaluated locally and each one that changed is
//      marked `refreshed`, with the alert-time counts kept beside the new ones.
//
// The IP row's verdict follows the display priority (exact hit in either window → match; else a /24 or /64
// neighbour → partial, NOT counted; else deviation; else no_data). Before v1.193.0 the two page copies
// coloured it by that priority but COUNTED it from the raw matches, so the header could disagree with the row.

import { getSigninDeviceContext, getEventUtcHour, canonicalOsLabel, findBaselineSubnetNeighbor } from './eventUtils.js';
import { matchCountryListIso2 } from './countryNorm.js';
import { baselineVerdict } from './baselineVerdict.js';

export const BASELINE_COMPARISON_SCHEMA_VERSION = 1;
export const EMPTY_EVENT = '—';
export const EMPTY_BASELINE = 'No baseline data';
const MAX_DISPLAY_CHARS = 500;

export const ROW_LABELS = Object.freeze({
  country: 'Country',
  provider: 'Provider',
  state: 'State',
  city: 'City',
  ip: 'IP in baseline',
  hour_utc: 'Hour of day (UTC)',
  os: 'OS',
  intune_managed: 'Intune Managed device',
  intune_compliant: 'Intune Compliant device',
  device_in_drmm: 'Device in DRMM',
});
export const ROW_KEYS = Object.freeze(Object.keys(ROW_LABELS));
// The four rows whose event value an IP-enrichment refresh can change.
export const REFRESHABLE_KEYS = Object.freeze(['country', 'provider', 'state', 'city']);

// ── Small helpers (each mirrors a pipeline _bc_* function) ──────────────────────────────────────────────

const blank = (v) => v == null || v === '';
const splitCsv = (s) => (s ? String(s).split(',').map((v) => v.trim()).filter(Boolean) : []);

function disp(v, empty) {
  if (blank(v)) return empty;
  const s = String(v);
  return s.length <= MAX_DISPLAY_CHARS ? s : `${s.slice(0, MAX_DISPLAY_CHARS - 1)}…`;
}

// Strip a port: "[v6]:port" → v6; "v4:port" (exactly one colon, digits after it) → v4; a bare IPv6 is kept.
export function stripIpPort(ipPort) {
  if (!ipPort) return '';
  const ip = String(ipPort);
  if (ip.startsWith('[')) {
    const close = ip.indexOf(']');
    if (close !== -1) return ip.slice(1, close);
  }
  const colon = ip.lastIndexOf(':');
  if (colon !== -1 && /^\d+$/.test(ip.slice(colon + 1)) && ip.indexOf(':') === colon) return ip.slice(0, colon);
  return ip;
}

// The colour rule lives in baselineVerdict.js (and its test); this maps it to the contract's verdict names.
const VERDICT_BY_COLOUR = { green: 'match', amber: 'partial', red: 'deviation', gray: 'no_data' };
const verdictFromMatches = (m7, m30) => VERDICT_BY_COLOUR[baselineVerdict(m7, m30)];

function matchProvider(val, s) {
  if (val == null || !s) return null;
  const target = String(val).trim().toLowerCase();
  return target ? String(s).toLowerCase().includes(target) : null;
}

function matchText(ev, bl) {
  if (blank(ev) || blank(bl)) return null;
  return String(bl).toLowerCase() === String(ev).toLowerCase();
}

function matchList(val, s) {
  if (val == null || !s) return null;
  const list = splitCsv(s);
  return list.length ? list.some((v) => v.toLowerCase() === String(val).toLowerCase()) : null;
}

const normalizeOs = (s) => String(s).toLowerCase().replace(/[\s\d._]+$/, '').trim();

function osTypes(snap) {
  if (Array.isArray(snap.os_breakdown) && snap.os_breakdown.length) {
    return snap.os_breakdown.filter((o) => o && typeof o === 'object' && o.os_type).map((o) => o.os_type);
  }
  if (Array.isArray(snap.os_list) && snap.os_list.length) return [...snap.os_list];
  return snap.os_type ? [snap.os_type] : [];
}

function osInBaseline(ev, snap) {
  if (!ev || !snap) return null;
  const types = osTypes(snap);
  if (!types.length) return null;
  const target = normalizeOs(ev);
  return types.some((t) => normalizeOs(t) === target);
}

function osCaption(snap) {
  if (!snap) return null;
  const bd = snap.os_breakdown;
  if (Array.isArray(bd) && bd.length > 1) {
    return bd.filter((o) => o && typeof o === 'object')
      .map((o) => {
        const n = Number(o.pct);
        return `${o.os_type} (${Number.isFinite(n) ? Math.floor(n + 0.5) : o.pct}%)`;
      })
      .join(', ');
  }
  const ol = snap.os_list;
  if (Array.isArray(ol) && ol.length > 1) return ol.map(String).join(', ');
  return snap.os_type ?? null;
}

function toInt(v) {
  if (v == null || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

const pad2 = (n) => String(n).padStart(2, '0');
const yesNo = (v) => (v == null ? null : v ? 'Yes' : 'No');
const usually = (v) => (v == null ? null : v ? 'Usually Yes' : 'Usually No');
const matchBool = (ev, bl) => (ev == null || bl == null ? null : typeof bl === 'boolean' && ev === bl);

function parseObj(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value || value.startsWith('blob:') || value.startsWith('_too_large')) return null;
  try {
    const o = JSON.parse(value);
    return o && typeof o === 'object' && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
}

/** The two baseline windows from a parsed baseline_context_json (7d falls back to the flat top level). */
export function baselineWindows(ctx) {
  if (!ctx) return { b7: null, b30: null };
  const s7 = ctx['7d_snapshot'];
  const s30 = ctx['30d_snapshot'];
  return {
    b7: s7 && typeof s7 === 'object' ? s7 : ctx,
    b30: s30 && typeof s30 === 'object' ? s30 : {},
  };
}

// ── The comparison ──────────────────────────────────────────────────────────────────────────────────────

/**
 * Build the v1 comparison from the alert's own fields — the pipeline's build_baseline_comparison_json.
 * Returns null when there is no usable baseline (the panel then says so).
 *
 *   baselineContext  baseline_context_json (string or parsed object)
 *   rawEvent         raw_event_json (string or parsed object)
 *   correlatedSignin correlated_signin_json (string or parsed object)
 *   alert            { ipCountry, ipProvider, ipState, ipCity, clientIp } — the row's own columns, with any
 *                    IP-refresh overlay already applied by the caller (see applyEnrichmentOverride)
 */
export function buildBaselineComparison({ baselineContext, rawEvent, correlatedSignin, alert = {} }) {
  const ctx = parseObj(baselineContext);
  if (!ctx || !Object.keys(ctx).length) return null;   // missing, spilled, unparseable or {} → no baseline
  const { b7, b30 } = baselineWindows(ctx);
  const raw = parseObj(rawEvent) || {};
  const correlated = parseObj(correlatedSignin);
  const loc = raw.location && typeof raw.location === 'object' ? raw.location : {};
  const first = (...vals) => vals.find((v) => v != null) ?? null;
  const csvDisp = (snap, field) => (snap[field] ? splitCsv(snap[field]).join(', ') : null);

  const rows = [];
  const add = (key, event, d7, d30, m7, m30, verdict = null, counted = true) => {
    rows.push({
      key,
      label: ROW_LABELS[key],
      event: disp(event, EMPTY_EVENT),
      baseline_7d: disp(d7, EMPTY_BASELINE),
      baseline_30d: disp(d30, EMPTY_BASELINE),
      match_7d: m7,
      match_30d: m30,
      verdict: verdict ?? verdictFromMatches(m7, m30),
      counted,
    });
  };

  const evCountry = first(loc.countryOrRegion, alert.ipCountry);
  add('country', evCountry, csvDisp(b7, 'country_list'), csvDisp(b30, 'country_list'),
    matchCountryListIso2(evCountry, b7.country_list), matchCountryListIso2(evCountry, b30.country_list));
  const evProvider = alert.ipProvider ?? null;
  add('provider', evProvider, csvDisp(b7, 'provider_list'), csvDisp(b30, 'provider_list'),
    matchProvider(evProvider, b7.provider_list), matchProvider(evProvider, b30.provider_list));
  for (const [key, locKey, alertKey, field] of [['state', 'state', 'ipState', 'typical_state'], ['city', 'city', 'ipCity', 'typical_city']]) {
    const ev = first(loc[locKey], alert[alertKey]);
    add(key, ev, b7[field], b30[field], matchText(ev, b7[field]), matchText(ev, b30[field]));
  }

  // IP — verdict from the display priority; a neighbour-only result is partial and not counted.
  let evIp = raw.ipAddress;
  if (evIp == null) evIp = alert.clientIp ? stripIpPort(alert.clientIp) : null;
  evIp = evIp || null;
  const l7 = b7.signin_ip_list;
  const l30 = b30.signin_ip_list;
  const x7 = matchList(evIp, l7);
  const x30 = matchList(evIp, l30);
  const n7 = x7 === true ? null : findBaselineSubnetNeighbor(evIp, l7);
  const n30 = x30 === true ? null : findBaselineSubnetNeighbor(evIp, l30);
  let ipVerdict = 'no_data';
  let ipCounted = true;
  if (x7 === true || x30 === true) ipVerdict = 'match';
  else if (n7 || n30) { ipVerdict = 'partial'; ipCounted = false; }
  else if (evIp && (l7 || l30)) ipVerdict = 'deviation';
  const ipDisp = (csv, neighbor) => {
    if (!csv) return null;
    const base = `${splitCsv(csv).length} known IPs`;
    return neighbor ? `${base} · ${neighbor} same subnet` : base;
  };
  add('ip', evIp, ipDisp(l7, n7), ipDisp(l30, n30), x7, x30, ipVerdict, ipCounted);

  // Hour of day (UTC), inclusive; the ranges are a min and a max, so they never wrap midnight.
  const hour = getEventUtcHour(raw);
  const range = (snap) => [toInt(snap.earliest_hour_utc), toInt(snap.latest_hour_utc)];
  const hourMatch = ([lo, hi]) => (hour == null || lo == null || hi == null ? null : lo <= hour && hour <= hi);
  const rangeDisp = ([lo, hi]) => (lo != null && hi != null ? `${pad2(lo)}:00–${pad2(hi)}:59` : null);
  const r7 = range(b7);
  const r30 = range(b30);
  add('hour_utc', hour != null ? `${pad2(hour)}:00` : null, rangeDisp(r7), rangeDisp(r30), hourMatch(r7), hourMatch(r30));

  // OS / Intune managed / Intune compliant — the first device source with any posture field wins outright.
  const { os: evOs, compliant: evComp, managed: evMgd } = getSigninDeviceContext(raw, correlated);
  add('os', canonicalOsLabel(evOs), osCaption(b7), Object.keys(b30).length ? osCaption(b30) : null,
    osInBaseline(evOs, b7), osInBaseline(evOs, b30));
  add('intune_managed', yesNo(evMgd), usually(b7.normally_managed), usually(b30.normally_managed),
    matchBool(evMgd, b7.normally_managed), matchBool(evMgd, b30.normally_managed));
  add('intune_compliant', yesNo(evComp), usually(b7.normally_compliant), usually(b30.normally_compliant),
    matchBool(evComp, b7.normally_compliant), matchBool(evComp, b30.normally_compliant));

  // Device in DRMM — event-only; shown only when the event names a device. No = partial, never counted.
  const dd = raw.deviceDetail && typeof raw.deviceDetail === 'object' ? raw.deviceDetail : {};
  const inDrmm = raw._device_in_drmm != null && dd.displayName ? raw._device_in_drmm : null;
  add('device_in_drmm', yesNo(inDrmm), null, null, null, null,
    inDrmm == null ? 'no_data' : inDrmm ? 'match' : 'partial', false);

  const window = (snap) => (!snap || (snap.window_start_utc == null && snap.window_end_utc == null)
    ? null : { start: snap.window_start_utc ?? null, end: snap.window_end_utc ?? null });
  const counts = countRows(rows);
  return {
    schema_version: BASELINE_COMPARISON_SCHEMA_VERSION,
    deviation_count: counts.deviations,
    partial_count: counts.partials,
    summary: summaryText(counts.deviations, counts.partials),
    windows: { '7d': window(b7), '30d': window(b30) },
    rows,
  };
}

export function countRows(rows) {
  let deviations = 0;
  let partials = 0;
  for (const r of rows || []) {
    if (!r.counted) continue;
    if (r.verdict === 'deviation') deviations++;
    else if (r.verdict === 'partial') partials++;
  }
  return { deviations, partials };
}

export function summaryText(deviations, partials) {
  const parts = [];
  if (deviations) parts.push(`${deviations} deviation${deviations !== 1 ? 's' : ''}`);
  if (partials) parts.push(`${partials} partial`);
  return parts.join(' · ');
}

/** The stored comparison when it is a usable schema_version 1 object, else null (use the fallback). */
export function parseStoredBaselineComparison(json) {
  const o = parseObj(json);
  if (!o || o.schema_version !== BASELINE_COMPARISON_SCHEMA_VERSION || !Array.isArray(o.rows)) return null;
  const rows = o.rows.filter((r) => r && typeof r === 'object' && typeof r.key === 'string');
  if (!rows.length) return null;
  return { ...o, rows };
}

/** The alert's columns with an IP-enrichment refresh overlay applied (the overlay's field names). */
export function applyEnrichmentOverride(alert, override) {
  return {
    ipCountry: override?.ipCountry ?? alert?.ipCountry ?? null,
    ipProvider: override?.ipProvider ?? alert?.ipProvider ?? null,
    ipState: override?.regionName ?? alert?.ipState ?? null,
    ipCity: override?.city ?? alert?.ipCity ?? null,
    clientIp: alert?.clientIp ?? null,
  };
}

/**
 * What the panel shows. Returns:
 *   { source: 'stored' | 'computed' | 'none', rows, deviations, partials, windows,
 *     alertTime: { deviations, partials } | null   — set only when a refresh changed a stored row,
 *     refreshedKeys: string[], ipLists: { '7d': string[], '30d': string[] } | null }
 */
export function resolveBaselineComparison({ storedJson, baselineContext, rawEvent, correlatedSignin, alert, enrichOverride }) {
  const ctx = parseObj(baselineContext);
  const { b7, b30 } = baselineWindows(ctx);
  const ipLists = ctx ? { '7d': splitCsv(b7.signin_ip_list), '30d': splitCsv(b30.signin_ip_list) } : null;
  const fields = applyEnrichmentOverride(alert, enrichOverride);
  const stored = parseStoredBaselineComparison(storedJson);

  if (!stored) {
    const built = buildBaselineComparison({ baselineContext: ctx, rawEvent, correlatedSignin, alert: fields });
    if (!built) return { source: 'none', rows: [], deviations: 0, partials: 0, windows: null, alertTime: null, refreshedKeys: [], ipLists: null };
    const c = countRows(built.rows);
    return { source: 'computed', rows: built.rows, deviations: c.deviations, partials: c.partials, windows: built.windows, alertTime: null, refreshedKeys: [], ipLists };
  }

  // Stored: the pipeline's rows as they are, in contract order; a missing key reads as no_data rather than
  // silently disappearing (the contract sends all 10).
  const byKey = Object.fromEntries(stored.rows.map((r) => [r.key, r]));
  let rows = ROW_KEYS.map((key) => byKey[key] ? { label: ROW_LABELS[key], ...byKey[key] } : {
    key, label: ROW_LABELS[key], event: EMPTY_EVENT, baseline_7d: EMPTY_BASELINE, baseline_30d: EMPTY_BASELINE,
    match_7d: null, match_30d: null, verdict: 'no_data', counted: false,
  });
  // Any extra rows the pipeline adds later are kept, after ours (adding a row needs no version bump).
  rows = rows.concat(stored.rows.filter((r) => !ROW_KEYS.includes(r.key)).map((r) => ({ label: r.label || r.key, ...r })));

  const refreshedKeys = [];
  if (enrichOverride && ctx) {
    const rebuilt = buildBaselineComparison({ baselineContext: ctx, rawEvent, correlatedSignin, alert: fields });
    const fresh = Object.fromEntries((rebuilt?.rows || []).map((r) => [r.key, r]));
    rows = rows.map((r) => {
      const f = fresh[r.key];
      if (!REFRESHABLE_KEYS.includes(r.key) || !f) return r;
      if (f.event === r.event && f.verdict === r.verdict) return r;
      refreshedKeys.push(r.key);
      return { ...r, event: f.event, match_7d: f.match_7d, match_30d: f.match_30d, verdict: f.verdict, counted: f.counted, refreshed: true };
    });
  }
  const c = countRows(rows);
  const alertTime = refreshedKeys.length ? countRows(stored.rows) : null;
  return { source: 'stored', rows, deviations: c.deviations, partials: c.partials, windows: stored.windows || null, alertTime, refreshedKeys, ipLists };
}

const VERDICT_CLASS = {
  match: 'text-green-400 font-mono',
  partial: 'text-amber-400 font-mono',
  deviation: 'text-red-400 font-mono',
  no_data: 'text-gray-300 font-mono',
};
/** Tailwind class for a row's Event value cell. An unknown verdict reads as no colour, never as a match. */
export function verdictCellClass(verdict) {
  return VERDICT_CLASS[verdict] || VERDICT_CLASS.no_data;
}
